import type { ExpenseListItem } from "@apps/dashboard/lib/home-expense-types";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import { splitMinorByShareBps } from "@apps/dashboard/lib/expense-splits";
import type { Locale } from "@project/i18n";
import { localeToIntlTag } from "@project/i18n";
import { localDateToIsoDate, sortJournalExpensesForMonth } from "@project/core";

export type MonthCurrencyTotal = { currency: string; amountMinor: number };

export type MonthMemberShare = {
  userId: string;
  name: string;
  byCurrency: MonthCurrencyTotal[];
};

export type ExpenseMonthGroup = {
  yearMonth: string;
  items: ExpenseListItem[];
  /** Progreso real: solo gastos pagados, importe efectivo. */
  totalsByCurrency: MonthCurrencyTotal[];
  /** Previsión del mes: todos los gastos, importe efectivo. */
  forecastTotalsByCurrency: MonthCurrencyTotal[];
  /** Suma de cuotas según `splits` (solo pagados). */
  memberShares: MonthMemberShare[];
  /** Reparto con todos los gastos del mes (previsión). */
  forecastMemberShares: MonthMemberShare[];
};

function yearMonthFromOccurredOn(occurredOn: string): string {
  return occurredOn.length >= 7 ? occurredOn.slice(0, 7) : occurredOn;
}

function addMinor(map: Map<string, number>, currency: string, delta: number): void {
  map.set(currency, (map.get(currency) ?? 0) + delta);
}

/** Mes calendario actual en zona local (`YYYY-MM`), alineado con cómo el usuario ve las fechas del diario. */
export function currentYearMonthLocal(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Orden de bloques mensuales: mes actual → meses futuros (cronológico) → meses pasados (más reciente primero).
 */
function orderedYearMonths(
  keys: string[],
  nowYm: string,
): string[] {
  const current: string[] = [];
  const future: string[] = [];
  const past: string[] = [];
  for (const ym of keys) {
    if (ym === nowYm) {
      current.push(ym);
    } else if (ym > nowYm) {
      future.push(ym);
    } else {
      past.push(ym);
    }
  }
  future.sort((a, b) => a.localeCompare(b));
  past.sort((a, b) => b.localeCompare(a));
  return [...current, ...future, ...past];
}

/**
 * Agrupa gastos por `occurredOn` (YYYY-MM), ordena meses (actual → futuro → pasado) y calcula
 * totales por moneda y aportación acumulada por miembro (solo gastos con `splits`).
 */
export function buildExpenseMonthGroups(
  expenses: ExpenseListItem[],
  memberIdToName: Map<string, string>,
  options?: { currentYearMonth?: string },
): ExpenseMonthGroup[] {
  const nowYm = options?.currentYearMonth ?? currentYearMonthLocal();

  const byMonth = new Map<string, ExpenseListItem[]>();
  for (const e of expenses) {
    const ym = yearMonthFromOccurredOn(e.occurredOn);
    const arr = byMonth.get(ym) ?? [];
    arr.push(e);
    byMonth.set(ym, arr);
  }

  const yearMonths = orderedYearMonths([...byMonth.keys()], nowYm);

  const todayIso = localDateToIsoDate();
  return yearMonths.map((yearMonth) => {
    const rawItems = byMonth.get(yearMonth) ?? [];
    const items = sortJournalExpensesForMonth(rawItems, yearMonth, {
      currentYearMonth: nowYm,
      todayIso,
    });
    const paidTotalsMap = new Map<string, number>();
    const forecastTotalsMap = new Map<string, number>();
    const paidMemberMap = new Map<string, { name: string; amounts: Map<string, number> }>();
    const forecastMemberMap = new Map<string, { name: string; amounts: Map<string, number> }>();

    for (const e of items) {
      const eff = expenseEffectiveMinor(e);
      addMinor(forecastTotalsMap, e.currency, eff);
      if (e.paidAt) {
        addMinor(paidTotalsMap, e.currency, eff);
      }

      const splits = e.splits ?? [];
      if (splits.length === 0) continue;
      const splitInputs = splits.map((s) => ({
        userId: s.userId,
        shareBps: s.shareBps,
      }));
      const forecastRows = splitMinorByShareBps(eff, splitInputs, (uid) =>
        memberIdToName.get(uid) ?? uid,
      );
      for (const row of forecastRows) {
        let entry = forecastMemberMap.get(row.userId);
        if (!entry) {
          entry = { name: row.name, amounts: new Map() };
          forecastMemberMap.set(row.userId, entry);
        }
        addMinor(entry.amounts, e.currency, row.amountMinor);
      }
      if (e.paidAt) {
        const paidRows = splitMinorByShareBps(eff, splitInputs, (uid) =>
          memberIdToName.get(uid) ?? uid,
        );
        for (const row of paidRows) {
          let entry = paidMemberMap.get(row.userId);
          if (!entry) {
            entry = { name: row.name, amounts: new Map() };
            paidMemberMap.set(row.userId, entry);
          }
          addMinor(entry.amounts, e.currency, row.amountMinor);
        }
      }
    }

    const totalsByCurrency = [...paidTotalsMap.entries()]
      .map(([currency, amountMinor]) => ({ currency, amountMinor }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    const forecastTotalsByCurrency = [...forecastTotalsMap.entries()]
      .map(([currency, amountMinor]) => ({ currency, amountMinor }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    const memberShares = [...paidMemberMap.entries()]
      .map(([userId, { name, amounts }]) => ({
        userId,
        name,
        byCurrency: [...amounts.entries()]
          .map(([currency, amountMinor]) => ({ currency, amountMinor }))
          .sort((a, b) => a.currency.localeCompare(b.currency)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    const forecastMemberShares = [...forecastMemberMap.entries()]
      .map(([userId, { name, amounts }]) => ({
        userId,
        name,
        byCurrency: [...amounts.entries()]
          .map(([currency, amountMinor]) => ({ currency, amountMinor }))
          .sort((a, b) => a.currency.localeCompare(b.currency)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return {
      yearMonth,
      items,
      totalsByCurrency,
      forecastTotalsByCurrency,
      memberShares,
      forecastMemberShares,
    };
  });
}

export function formatYearMonthTitle(ym: string, locale: Locale): string {
  const parts = ym.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  if (y == null || m == null || Number.isNaN(y) || Number.isNaN(m)) {
    return ym;
  }
  const d = new Date(Date.UTC(y, m - 1, 1));
  const tag = localeToIntlTag(locale);
  const raw = new Intl.DateTimeFormat(tag, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
